import { buildFilterFields } from '@/utils/objectHelper'
import apiWrapper from '@/utils/apiHelpers/apiWrapper'
import { groupBy, prop, sort, descend, ascend } from 'ramda'
import moment from 'moment'
import { customerHelper, generalConstants } from '@tweddle-se-team/common/constants'
import { customerId } from '@/utils/env'

const { stringify } = JSON
const { CUSTOMER_IDS } = customerHelper
const { WTY } = generalConstants

const filterEmptyFields = buildFilterFields(x => x)

const publicationBackLabelByCustomer = {
  [CUSTOMER_IDS.TME]: t => t('content_go_back', 'Back'),
  [CUSTOMER_IDS.SUBARU]: t => t('content_go_back', 'Back'),
  [CUSTOMER_IDS.NORDICS]: t => t('pubications_manuals', 'Manuals')
}

const filtersToString = ({
  brand,
  model,
  modelType,
  year,
  publicationType,
  language,
  partNumber,
  lineOffDate,
  katashiki,
  dateFrom,
  dateTo
}) =>
  stringify(
    filterEmptyFields({
      brand,
      model,
      modelType,
      year,
      publicationType,
      language,
      partNumber,
      lineOffDate,
      katashiki,
      dateFrom,
      dateTo
    })
  )

export const getPublications = (filters, pagination = {}, ctx = null) => {
  const sort = pagination?.sort && stringify(pagination.sort)
  return apiWrapper.get('publications', { filters: filtersToString(filters), ...pagination, sort }, ctx)
}

export const buildServiceWarrantyQuery = (routerQuery, year, swFormat) => {
  const query = { ...routerQuery, swFormat }

  Object.keys(query).forEach(key => {
    if (key === 'year' || /^year\[\d+\]$/.test(key)) delete query[key]
  })

  if (year) query.year = year

  return query
}

export const sortPublicationsInOrderToPriority = (publications, productionDate) => {
  const groupedPublications = groupBy(prop('publicationType'), publications)

  // TB-9950: DIVA: Vin populate results - for customerportal, we need to show all publications. Uncomment the following lines if we need to skip UG publications when OM exists
  // if (groupedPublications.UG && groupedPublications.OM) {
  //   // skip UG if OM exists
  //   groupedPublications.OM = [...groupedPublications.OM, ...groupedPublications.UG]
  //   delete groupedPublications.UG
  // }

  Object.keys(groupedPublications).forEach(key => {
    const publicationsAfterProductionDate = sort(
      ascend(prop('lineOffDate')),
      groupedPublications[key].filter(
        pub =>
          moment(new Date(pub.lineOffDate)).valueOf() > parseInt(productionDate) &&
          (pub.contents.ditaId || pub.contents.webview)
      )
    )
    const publicationsBeforeProductionDate = sort(
      descend(prop('lineOffDate')),
      groupedPublications[key].filter(
        pub =>
          moment(new Date(pub.lineOffDate)).valueOf() <= parseInt(productionDate) &&
          (pub.contents.ditaId || pub.contents.webview)
      )
    )

    groupedPublications[key] = publicationsBeforeProductionDate.length
      ? publicationsBeforeProductionDate[0]
      : publicationsAfterProductionDate[0]
  }, {})

  return Object.values(groupedPublications)
}

/**
 * Query builders per customer
 * Each function builds the appropriate query params for navigation back to publications list
 */
const queryBuilders = {
  [CUSTOMER_IDS.SUBARU]: ({ publication, overviewOptions }) => {
    const { brand, model, modelType, year, language } = publication
    return {
      brand,
      model,
      modelType,
      year,
      language,
      ...overviewOptions
    }
  },

  [CUSTOMER_IDS.NORDICS]: ({ publication, overviewOptions }) => {
    const { brand, model, modelType, year, language } = publication
    return {
      brand,
      model,
      modelType,
      year,
      language,
      ...overviewOptions
    }
  },

  [CUSTOMER_IDS.TME]: ({ publication, overviewOptions, search, requestedLanguage, countryOfDeliveryCode }) => {
    const { brand, model, modelType, language: publicationLanguage, publicationType } = publication
    const filter = search?.query?.filterBy || {}
    const language =
      publicationType === WTY ? requestedLanguage || filter.language || publicationLanguage : publicationLanguage

    const query = {
      brand,
      model,
      modelType,
      language,
      cod: countryOfDeliveryCode || filter.cod,
      ngtdModelId: filter.ngtdModelId,
      generationId: filter.generationId,
      katashiki: filter.katashiki,
      productionDate: filter.productionDate,
      dateFrom: filter.dateFrom,
      dateTo: filter.dateTo,
      ...overviewOptions
    }

    // TB-9530: Only include year if it was in the original search filters
    // Don't derive it from lineOffDate as it may narrow results unnecessarily
    if (filter.year) {
      query.year = filter.year
    }

    // TB-9167: If katashiki is present, remove year filter
    // Katashiki holds year information and results can spread over multiple years
    if (query.katashiki) {
      delete query.year
    }

    return query
  }
}

/**
 * Build the query params for navigating back to publications list
 * Automatically selects the appropriate strategy based on build-time customerId
 *
 * @param {Object} publication - Publication data with brand, model, modelType, etc.
 * @param {Object} overviewOptions - Overview options from redux (view, hotspots, etc.)
 * @param {Object} search - Search state with filterBy data (Toyota only)
 * @returns {Object} Query params for publications route
 */
export const buildBackQuery = ({
  publication = {},
  overviewOptions = {},
  search = {},
  requestedLanguage,
  countryOfDeliveryCode
}) => {
  const builder = queryBuilders[customerId] || queryBuilders[CUSTOMER_IDS.TME]
  return builder({ publication, overviewOptions, search, requestedLanguage, countryOfDeliveryCode })
}

export const getPublicationBackLabel = t => {
  const getLabel = publicationBackLabelByCustomer[customerId] || publicationBackLabelByCustomer[CUSTOMER_IDS.TME]
  return getLabel(t)
}
